import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Modal from '../../components/common/Modal';
import { timeEntryService } from '../../services/api';
import { formatHoursMinutes } from '../../utils/formatHours';

// Customers read worked time as H:MM. Serialized entries already ship a
// formatted duration; the decimal `hours` field is the fallback.
const entryDuration = (entry) => entry?.durationFormatted || formatHoursMinutes(entry?.hours);

// Table styles
const TableContainer = styled.div`
  overflow-x: auto;
  margin-top: ${({ theme }) => theme.spacing[4]};
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHead = styled.thead`
  background-color: ${({ theme }) => theme.colors.light};
`;

const TableHeadCell = styled.th`
  padding: ${({ theme }) => theme.spacing[3]};
  text-align: left;
  font-weight: ${({ theme }) => theme.typography.fontWeight.semiBold};
  color: ${({ theme }) => theme.colors.dark};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  white-space: nowrap;
`;

const TableRow = styled.tr`
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};

  &:hover {
    background-color: ${({ theme }) => theme.colors.lightAlt};
  }
`;

const TableCell = styled.td`
  padding: ${({ theme }) => theme.spacing[3]};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.dark};
`;

// Status badge
const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.25rem 0.5rem;
  font-size: 0.75rem;
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  border-radius: 9999px;

  ${({ status, theme }) => {
    if (status === 'approved') {
      return `background-color: var(--status-approved-plate); color: ${theme.colors.success};`;
    } else if (status === 'pending') {
      return `background-color: var(--status-warning-plate); color: ${theme.colors.warning};`;
    } else if (status === 'challenged') {
      return `background-color: rgba(195,37,29,.25); color: ${theme.colors.error};`;
    }
    return '';
  }}
`;

const ActionLink = styled(Link)`
  color: ${({ theme }) => theme.colors.primary};
  text-decoration: none;
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};

  &:hover {
    text-decoration: underline;
  }
`;

const ActionButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme, danger }) => (danger ? theme.colors.error : theme.colors.primary)};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  cursor: pointer;
  padding: 0;
  font-size: ${({ theme }) => theme.typography.fontSize.sm};

  &:hover { text-decoration: underline; }
  &:disabled { opacity: 0.6; cursor: not-allowed; }

  @media (max-width: 576px) {
    min-height: 44px;
    padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[3]};
  }
`;

/* Pending approvals: table on desktop, stacked cards on phones (CSS-only toggle) */
const DesktopTableContainer = styled(TableContainer)`
  @media (max-width: 576px) { display: none; }
`;

const MobileCards = styled.div`
  display: none;

  @media (max-width: 576px) {
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing[3]};
    margin-top: ${({ theme }) => theme.spacing[4]};
  }
`;

const MobileCard = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borders.radius.md};
  padding: ${({ theme }) => theme.spacing[3]};
  background-color: ${({ theme }) => theme.colors.white};
`;

const MobileCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semiBold};
  color: ${({ theme }) => theme.colors.dark};
`;

const MobileCardMeta = styled.div`
  margin-top: ${({ theme }) => theme.spacing[1]};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.medium};
`;

const MobileCardActions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[2]};
  margin-top: ${({ theme }) => theme.spacing[3]};

  ${ActionButton},
  ${ActionLink} {
    flex: 1;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-height: 44px;
    padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[3]};
    border: 1px solid ${({ theme }) => theme.colors.border};
    border-radius: ${({ theme }) => theme.borders.radius.md};
    text-decoration: none;
  }
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing[3]};
  margin-top: ${({ theme }) => theme.spacing[4]};

  @media (max-width: 576px) {
    flex-direction: column-reverse;
    gap: ${({ theme }) => theme.spacing[2]};
    & > * { width: 100%; }
  }
`;

const FieldLabel = styled.label`
  display: block;
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.dark};
  margin-top: ${({ theme }) => theme.spacing[3]};
`;

const HoursInput = styled.input`
  width: 140px;
  padding: ${({ theme }) => theme.spacing[3]};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borders.radius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  margin-top: ${({ theme }) => theme.spacing[2]};
  &:focus { border-color: ${({ theme }) => theme.colors.primary}; outline: none; box-shadow: var(--focus-act); }
`;

// The challenge field stays DECIMAL hours (challengeTimeEntry takes decimal
// hours); the H:MM reading is echoed beside it so it agrees with the list.
const HoursEquiv = styled.span`
  margin-left: ${({ theme }) => theme.spacing[2]};
  color: ${({ theme }) => theme.colors.medium};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
`;

const ReasonTextarea = styled.textarea`
  width: 100%;
  padding: ${({ theme }) => theme.spacing[3]};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borders.radius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  min-height: 100px;
  resize: vertical;
  margin-top: ${({ theme }) => theme.spacing[2]};

  &:focus { border-color: ${({ theme }) => theme.colors.primary}; outline: none; box-shadow: var(--focus-act); }
`;

const ModalError = styled.div`
  color: ${({ theme }) => theme.colors.error};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  margin-top: ${({ theme }) => theme.spacing[2]};
`;

const Dashboard = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [actionModal, setActionModal] = useState({ open: false, entry: null, action: '' });
  const [challengeReason, setChallengeReason] = useState('');
  const [challengeHours, setChallengeHours] = useState('');
  const [actionError, setActionError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Default view (REQ #15): everything awaiting this customer's approval —
  // employees and the days — without any hours/employee count widgets.
  const fetchDashboardData = useCallback(async () => {
    try {
      setError(null);
      const approvals = await timeEntryService.getPendingApprovals({ limit: 200 });
      setPendingApprovals(Array.isArray(approvals) ? approvals : []);
    } catch (err) {
      setError(t('customer.dashboard.loadError'));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const openActionModal = (entry, action) => {
    setActionModal({ open: true, entry, action });
    setChallengeReason('');
    // Decimal on purpose: the field feeds the API's decimal `hours` parameter.
    setChallengeHours(entry ? String(entry.hours ?? '') : '');
    setActionError(null);
  };

  const closeActionModal = () => {
    setActionModal({ open: false, entry: null, action: '' });
    setChallengeReason('');
    setChallengeHours('');
    setActionError(null);
  };

  const handleConfirmAction = async () => {
    const { entry, action } = actionModal;
    if (!entry) return;

    if (action === 'challenge') {
      if (!challengeReason.trim()) {
        setActionError(t('customer.approvals.challengeReasonRequired'));
        return;
      }
      const h = Number(challengeHours);
      if (challengeHours !== '' && (!Number.isFinite(h) || h < 0 || h > 24)) {
        setActionError(t('customer.approvals.hoursRange', { defaultValue: 'Approved hours must be between 0 and 24' }));
        return;
      }
    }

    try {
      setSubmitting(true);
      setActionError(null);

      if (action === 'approve') {
        await timeEntryService.approveTimeEntry(entry.id);
      } else {
        await timeEntryService.challengeTimeEntry(entry.id, challengeReason.trim(), challengeHours === '' ? undefined : Number(challengeHours));
      }

      closeActionModal();
      await fetchDashboardData();
    } catch (err) {
      setActionError(
        t(action === 'approve' ? 'customer.dashboard.approveError' : 'customer.dashboard.challengeError')
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ marginBottom: '1.5rem' }}>
      {error && (
        <Card padding="md" elevation="sm" style={{ marginBottom: '1.5rem' }}>
          <div style={{ color: 'var(--act-danger)' }}>{error}</div>
        </Card>
      )}

      {/* Pending Approvals — the customer's home view (REQ #15) */}
      <Card
        title={t('customer.dashboard.statPendingApprovals')}
        subtitle={t('customer.dashboard.waitingReview')}
        headerActions={
          <Button variant="ghost" size="sm" as={Link} to="/customer/approvals">{t('customer.dashboard.viewAll')}</Button>
        }
        padding="md"
      >
        {loading ? (
          <div>{t('customer.dashboard.loadingApprovals')}</div>
        ) : (
          <>
            <DesktopTableContainer>
              <Table>
                <TableHead>
                  <tr>
                    <TableHeadCell>{t('common.employee')}</TableHeadCell>
                    <TableHeadCell>{t('common.date')}</TableHeadCell>
                    <TableHeadCell>{t('common.hours')}</TableHeadCell>
                    <TableHeadCell>{t('common.status')}</TableHeadCell>
                    <TableHeadCell>{t('common.actions')}</TableHeadCell>
                  </tr>
                </TableHead>
                <tbody>
                  {pendingApprovals.length > 0 ? (
                    pendingApprovals.map((entry) => (
                      <TableRow key={entry.id}>
                        <TableCell>{entry.employeeName || entry.employee?.name}</TableCell>
                        <TableCell>{entry.date}</TableCell>
                        <TableCell>{entryDuration(entry)}</TableCell>
                        <TableCell>
                          <Badge status={entry.status || 'pending'}>
                            {t(`status.${entry.status || 'pending'}`)}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div style={{ display: 'flex', gap: '0.75rem' }}>
                            <ActionButton onClick={() => openActionModal(entry, 'approve')}>
                              {t('customer.dashboard.approve')}
                            </ActionButton>
                            <ActionButton danger onClick={() => openActionModal(entry, 'challenge')}>
                              {t('customer.dashboard.challenge')}
                            </ActionButton>
                            <ActionLink to={`/customer/approvals/${entry.id}`}>
                              {t('customer.dashboard.review')}
                            </ActionLink>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5} style={{ textAlign: 'center' }}>
                        {t('customer.dashboard.noPendingApprovals')}
                      </TableCell>
                    </TableRow>
                  )}
                </tbody>
              </Table>
            </DesktopTableContainer>

            <MobileCards>
              {pendingApprovals.length > 0 ? (
                pendingApprovals.map((entry) => (
                  <MobileCard key={entry.id}>
                    <MobileCardHeader>
                      <span>{entry.employeeName || entry.employee?.name}</span>
                      <Badge status={entry.status || 'pending'}>
                        {t(`status.${entry.status || 'pending'}`)}
                      </Badge>
                    </MobileCardHeader>
                    <MobileCardMeta>
                      {entry.date} · {t('common.hours')}: {entryDuration(entry)}
                    </MobileCardMeta>
                    <MobileCardActions>
                      <ActionButton onClick={() => openActionModal(entry, 'approve')}>
                        {t('customer.dashboard.approve')}
                      </ActionButton>
                      <ActionButton danger onClick={() => openActionModal(entry, 'challenge')}>
                        {t('customer.dashboard.challenge')}
                      </ActionButton>
                      <ActionLink to={`/customer/approvals/${entry.id}`}>
                        {t('customer.dashboard.review')}
                      </ActionLink>
                    </MobileCardActions>
                  </MobileCard>
                ))
              ) : (
                <div style={{ textAlign: 'center' }}>
                  {t('customer.dashboard.noPendingApprovals')}
                </div>
              )}
            </MobileCards>
          </>
        )}
      </Card>

      {/* Approve / challenge confirmation modal */}
      {actionModal.open && (
        <Modal
          title={actionModal.action === 'approve'
            ? t('customer.dashboard.approveModalTitle')
            : t('customer.dashboard.challengeModalTitle')}
          onClose={closeActionModal}
          size="sm"
        >
          <p>
            {/* Dedicated H:MM variants of the confirm sentences: the original keys
                spell out "{{hours}} hours", which reads wrong as "7:30 hours". */}
            {actionModal.action === 'approve'
              ? t('customer.dashboard.confirmApproveHm', { defaultValue: 'Are you sure you want to approve this time entry ({{hours}} on {{date}})?', hours: entryDuration(actionModal.entry), date: actionModal.entry?.date })
              : t('customer.dashboard.confirmChallengeHm', { defaultValue: 'Are you sure you want to challenge this time entry ({{hours}} on {{date}})?', hours: entryDuration(actionModal.entry), date: actionModal.entry?.date })}
          </p>
          {actionModal.action === 'challenge' && (
            <>
              <FieldLabel>{t('customer.approvals.approvedHours', { defaultValue: 'Hours you approve (0–24)' })}</FieldLabel>
              <HoursInput
                type="number" min="0" max="24" step="0.25"
                value={challengeHours}
                onChange={(e) => setChallengeHours(e.target.value)}
              />
              <HoursEquiv>= {formatHoursMinutes(challengeHours)}</HoursEquiv>
              <FieldLabel>{t('customer.approvals.challengeReason')}</FieldLabel>
              <ReasonTextarea
                value={challengeReason}
                onChange={(e) => setChallengeReason(e.target.value)}
                placeholder={t('customer.dashboard.challengePlaceholder')}
              />
            </>
          )}
          {actionError && <ModalError>{actionError}</ModalError>}
          <ModalActions>
            <Button variant="outline" onClick={closeActionModal} disabled={submitting}>
              {t('common.cancel')}
            </Button>
            <Button
              variant={actionModal.action === 'approve' ? 'primary' : 'accent'}
              onClick={handleConfirmAction}
              disabled={submitting}
            >
              {submitting
                ? t('customer.dashboard.working')
                : actionModal.action === 'approve' ? t('customer.dashboard.approve') : t('customer.dashboard.challenge')}
            </Button>
          </ModalActions>
        </Modal>
      )}
    </div>
  );
};

export default Dashboard;
